import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

class ProductRecommendations extends Component {
    /**
     * The observer for the product recommendations
     * @type {IntersectionObserver}
     */
    #intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
            if (!entries[0]?.isIntersecting) return;

            observer.disconnect();
            this.#loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
    );

    /**
     * Observing changes to the elements attributes
     * @type {MutationObserver}
     */
    #mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            // Only attribute changes are interesting
            if (mutation.target !== this || mutation.type !== 'attributes') continue;

            // Ignore error attribute changes
            if (mutation.attributeName === 'data-error') continue;

            // Ignore addition of hidden class because it means there's an error with the display
            if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

            // Ignore when the data-recommendations-performed attribute has been set to 'true'
            if (
                mutation.attributeName === 'data-recommendations-performed' &&
                this.dataset.recommendationsPerformed === 'true'
            )
                continue;

            // All other attribute changes trigger a reload
            this.#loadRecommendations();
            break;
        }
    });

    /**
     * The cached recommendations
     * @type {Record<string, string>}
     */
    #cachedRecommendations = {};

    /**
     * An abort controller for the active fetch (if there is one)
     * @type {AbortController | null}
     */
    #activeFetch = null;

    connectedCallback() {
        super.connectedCallback();
        this.#intersectionObserver.observe(this);
        this.#mutationObserver.observe(this, { attributes: true });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#intersectionObserver.disconnect();
        this.#mutationObserver.disconnect();
    }

    /**
     * Load the product recommendations
     */
    #loadRecommendations() {
        const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
        const id = this.id;

        if (!productId || !id) {
            throw new Error('Product ID and an ID attribute are required');
        }

        // If the recommendations have already been loaded, accounts for the case where the Theme Editor
        // is loaded the section from the editor's visual preview context.
        if (recommendationsPerformed === 'true') {
            return;
        }

        this.#fetchCachedRecommendations(productId, sectionId, intent)
            .then((result) => {
                if (!result.success) {
                    // The Theme Editor will place a section element element in the DOM whose section_id is not available
                    // to the Section Renderer API. In this case, we can safely ignore the error.
                    if (!Shopify.designMode) {
                        this.#handleError(new Error(`Server returned ${result.status}`));
                    }
                    return;
                }

                const html = document.createElement('div');
                html.innerHTML = result.data || '';
                const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

                if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
                    this.dataset.recommendationsPerformed = 'true';
                    this.innerHTML = recommendations.innerHTML;
                    const recommendationCards = document.querySelectorAll(
                        '.product-recommendations .resource-list__item'
                    );

                    const moneyFormat = document.body.dataset.moneyFormat ?? '{{amount}}';
                    const currency = document.body.dataset.currency ?? 'INR';

                    recommendationCards.forEach((card) => {
                        const recommendationSwatches = card.querySelectorAll('.customize-product__swatch');
                        const variantText = card.querySelector('.product-form__variant-active p');

                        const priceWrapper = card.querySelector('product-price');
                        const comparePriceEl = priceWrapper?.querySelector('.compare-at-price');
                        const priceEl = priceWrapper?.querySelector('.price');

                        let selectedVarId = null;

                        function selectSwatch(/** @type {any} */ swatch) {
                            const name = swatch.dataset.name;
                            const varId = swatch.dataset.varid;
                            const price = parseInt(swatch.dataset.price || '0');
                            const compareAtPrice = parseInt(swatch.dataset.compareAtPrice || '0');
                            const discount = parseInt(swatch.dataset.discount || '0');

                            recommendationSwatches.forEach((s) => s.classList.remove('selectedColor'));
                            swatch.classList.add('selectedColor');

                            if (variantText) variantText.textContent = name;

                            selectedVarId = varId;

                            if (priceEl) {
                                let finalPrice = price;

                                // Find compare element
                                let compareEl = card.querySelector('.compare-at-price');

                                // CASE 1: Discount exists OR compareAtPrice is higher
                                if (discount > 0 || compareAtPrice > price) {
                                    let finalComparePrice = compareAtPrice;

                                    // If discount exists, apply it
                                    if (discount > 0) {
                                        let fixDiscount = discount * 100;
                                        finalPrice = Math.max(0, price - fixDiscount);

                                        // If no compareAtPrice provided, fallback to original price
                                        if (!compareAtPrice) {
                                            finalComparePrice = price;
                                        }
                                    }

                                    // Update main price
                                    priceEl.textContent = formatMoney(finalPrice, moneyFormat, currency);

                                    // Handle compare price UI
                                    if (compareEl instanceof HTMLElement) {
                                        compareEl.textContent = formatMoney(finalComparePrice, moneyFormat, currency);
                                        compareEl.style.display = '';
                                    } else {
                                        const priceContainer = card.querySelector('.price-nurtureand');

                                        if (priceContainer) {
                                            compareEl = document.createElement('span');
                                            compareEl.className = 'compare-at-price';

                                            priceContainer.insertBefore(compareEl, priceEl);

                                            compareEl.textContent = formatMoney(
                                                finalComparePrice,
                                                moneyFormat,
                                                currency
                                            );
                                            if (compareEl instanceof HTMLElement) compareEl.style.display = '';
                                        }
                                    }
                                } else {
                                    // CASE 2: No discount and no compare price
                                    priceEl.textContent = formatMoney(price, moneyFormat, currency);

                                    if (compareEl instanceof HTMLElement) {
                                        compareEl.style.display = 'none';
                                    }
                                }
                            }

                            let discountBadge = card.querySelector('.na-product__discount-badge');
                            const imageWrapper = card.querySelector(
                                '.customize-product__image.na-product-card-inline__img'
                            );
                            let finalDiscount = 0;
                            // Case 1: Use existing discount if available
                            if (discount > 0) {
                                finalDiscount = discount * 100;
                            }
                            // Case 2: Fallback to compareAtPrice logic
                            else if (compareAtPrice > price) {
                                finalDiscount = compareAtPrice - price;
                            }

                            // Show / Hide badge
                            if (finalDiscount > 0) {
                                if (discountBadge instanceof HTMLElement) {
                                    discountBadge.style.display = '';
                                    discountBadge.textContent = `${formatMoney(finalDiscount, moneyFormat, currency)} OFF`;
                                    discountBadge.classList.remove('hidden');
                                }
                            } else {
                                if (discountBadge instanceof HTMLElement) {
                                    discountBadge.style.display = 'none';
                                    discountBadge.classList.add('hidden');
                                }
                            }
                            card.setAttribute('data-selected-variant', varId);
                            card.setAttribute('data-option-value', name);

                            const imageUrl = swatch.dataset.imageChange;
                            if (imageUrl) {
                                const imageEl = card.querySelector('.customize-product__image img');

                                if (imageEl instanceof HTMLImageElement) {
                                    // Update src
                                    imageEl.src = imageUrl;

                                    // IMPORTANT: also update srcset
                                    imageEl.srcset = `${imageUrl} 1x`;

                                    // Optional: remove any lazy-loading conflicts
                                    imageEl.removeAttribute('data-src');
                                    imageEl.removeAttribute('data-srcset');
                                }
                            }
                        }

                        // Attach events
                        recommendationSwatches.forEach((swatch) => {
                            swatch.addEventListener('click', (e) => {
                                e.preventDefault();
                                selectSwatch(swatch);
                            });
                        });

                        // @ts-ignore
                        window.yotpoWidgetsContainer?.initWidgets();
                    });
                } else {
                    this.#handleError(new Error('No recommendations available'));
                }
            })
            .catch((e) => {
                this.#handleError(e);
            });
    }

    /**
     * Fetches the recommendations and cached the result for future use
     * @param {string} productId
     * @param {string | undefined} sectionId
     * @param {string | undefined} intent
     * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
     */
    async #fetchCachedRecommendations(productId, sectionId, intent) {
        const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

        const cachedResponse = this.#cachedRecommendations[url];
        if (cachedResponse) {
            return { success: true, data: cachedResponse };
        }

        this.#activeFetch?.abort();
        this.#activeFetch = new AbortController();

        try {
            const response = await fetch(url, { signal: this.#activeFetch.signal });
            if (!response.ok) {
                return { success: false, status: response.status };
            }

            const text = await response.text();
            this.#cachedRecommendations[url] = text;
            return { success: true, data: text };
        } finally {
            this.#activeFetch = null;
        }
    }

    /**
     * Handle errors in a consistent way
     * @param {Error} error
     */
    #handleError(error) {
        console.error('Product recommendations error:', error.message);
        this.classList.add('hidden');
        this.dataset.error = 'Error loading product recommendations';
    }
}

if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
}
